// ============================================================================
// PROJECT REQUIREMENTS HOOKS
// Fetch and manage project-level personas, user stories, tech specs
// ============================================================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';
import { useProjectSessions } from './useProjects';
import type { PersonaBackstory } from '../services/workshop';

// ============================================================================
// TYPES
// ============================================================================

export interface ProjectPersona {
  id: string;
  sessionId: string;
  name: string;
  role: string;
  goals: string[];
  painPoints: string[];
  context: string;
  backstory?: PersonaBackstory;
  createdAt?: string;
}

export { PersonaBackstory };

export interface ProjectUserStory {
  storyId: string;
  sessionId?: string;
  projectId?: string;
  level: 'session' | 'project';
  persona: string;
  story: string;
  acceptanceCriteria: string[];
  priority: 'high' | 'medium' | 'low';
  status?: 'draft' | 'accepted' | 'rejected' | 'implemented';
  generatedAt: string;
  sourceSessionId?: string;
  notes?: string[];
  source?: string;
  milestoneId?: string;
  createdBy?: string;
  createdAt?: string;
}

export interface TechnicalSpecs {
  coreFunctionality: string[];
  integrationPoints: string[];
  dataRequirements: string[];
  securityConsiderations: string[];
  performanceTargets: string[];
}

export interface ProjectRequirementsData {
  personas: ProjectPersona[];
  userStories: ProjectUserStory[];
  technicalSpecs: TechnicalSpecs;
  assumptions: string[];
  sessionCount: number;
}

// ============================================================================
// HELPERS
// ============================================================================

async function fetchSessionData(sessionId: string) {
  const [sessionRes, storiesRes] = await Promise.allSettled([
    api.get(`/api/sessions/${sessionId}`).catch(() => ({ data: {} })),
    api.get(`/api/sessions/${sessionId}/user-stories`).catch(() => ({ data: { userStories: [] } })),
  ]);

  const sessionData = sessionRes.status === 'fulfilled' ? (sessionRes.value as any)?.data : {};
  const extractedPersonas: any[] = sessionData?.extractedRequirements?.personas || [];
  const ddbPersonas: any[] = sessionData?.personas || [];

  const backstoryMap = new Map<string, PersonaBackstory>();
  for (const p of ddbPersonas) {
    if (p.id && p.backstory) backstoryMap.set(p.id, p.backstory);
  }

  const personas = extractedPersonas.length > 0
    ? extractedPersonas.map((p: any) => ({ ...p, backstory: backstoryMap.get(p.id) }))
    : ddbPersonas;

  const stories = storiesRes.status === 'fulfilled'
    ? (storiesRes.value as any)?.data?.userStories || []
    : [];

  return { sessionId, personas, stories, sessionData };
}

function mapPersona(p: any, sessionId: string): ProjectPersona {
  return {
    id: p.id || p.personaId,
    sessionId,
    name: p.name,
    role: p.role,
    goals: p.goals || [],
    painPoints: p.painPoints || [],
    context: p.context || '',
    backstory: p.backstory,
    createdAt: p.createdAt,
  };
}

function mapStory(s: any, sessionId: string): ProjectUserStory {
  return {
    storyId: s.storyId || s.id,
    sessionId,
    level: 'session',
    persona: s.persona,
    story: s.story,
    acceptanceCriteria: s.acceptanceCriteria || [],
    priority: s.priority || 'medium',
    status: s.status,
    generatedAt: s.generatedAt || s.createdAt || '',
    notes: s.notes,
    source: s.source,
    milestoneId: s.milestoneId,
    sourceSessionId: sessionId,
  };
}

function normalizeSpecItem(item: any): string {
  return typeof item === 'string' ? item : item.description || item.name || JSON.stringify(item);
}

function mergeExtractedRequirements(req: any, techSpecs: TechnicalSpecs, assumptionSet: Set<string>) {
  if (!req) return;
  if (req.technicalSpecs) {
    const keys = Object.keys(techSpecs) as (keyof TechnicalSpecs)[];
    for (const key of keys) {
      if (!req.technicalSpecs[key]) continue;
      const normalized = req.technicalSpecs[key].map(normalizeSpecItem);
      techSpecs[key] = [...techSpecs[key], ...normalized.filter((item: string) => !techSpecs[key].includes(item))];
    }
  }
  if (req.assumptions) {
    for (const a of req.assumptions) {
      assumptionSet.add(typeof a === 'string' ? a : a.assumption || JSON.stringify(a));
    }
  }
}

// ============================================================================
// FETCH ALL SESSION REQUIREMENTS (aggregated client-side)
// ============================================================================

export const useProjectRequirements = (projectId: string, token: string) => {
  const { data: sessions } = useProjectSessions(projectId);

  return useQuery<ProjectRequirementsData>({
    queryKey: ['project-requirements', projectId],
    queryFn: async () => {
      if (!sessions || sessions.length === 0) {
        return {
          personas: [],
          userStories: [],
          technicalSpecs: { coreFunctionality: [], integrationPoints: [], dataRequirements: [], securityConsiderations: [], performanceTargets: [] },
          assumptions: [],
          sessionCount: 0,
        };
      }

      const results = await Promise.allSettled(
        sessions.map((session) => fetchSessionData(session.sessionId))
      );

      const allPersonas: ProjectPersona[] = [];
      const allStories: ProjectUserStory[] = [];
      const techSpecs: TechnicalSpecs = { coreFunctionality: [], integrationPoints: [], dataRequirements: [], securityConsiderations: [], performanceTargets: [] };
      const assumptionSet = new Set<string>();
      let sessionCount = 0;

      for (const result of results) {
        if (result.status !== 'fulfilled') continue;
        const { sessionId, personas, stories, sessionData } = result.value;
        sessionCount++;

        for (const p of personas) {
          allPersonas.push(mapPersona(p, sessionId));
        }

        for (const s of stories) {
          allStories.push(mapStory(s, sessionId));
        }

        mergeExtractedRequirements(sessionData?.extractedRequirements, techSpecs, assumptionSet);
      }

      return { personas: allPersonas, userStories: allStories, technicalSpecs: techSpecs, assumptions: Array.from(assumptionSet), sessionCount };
    },
    enabled: !!projectId && !!sessions && sessions.length > 0,
    staleTime: 60000,
  });
};

// ============================================================================
// SYNC / AGGREGATE (triggers backend aggregation)
// ============================================================================

export const useUpdatePersonaBackstory = (projectId: string) => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, { sessionId: string; personaId: string; backstory: PersonaBackstory; name?: string; role?: string }>({
    mutationFn: async ({ sessionId, personaId, backstory, name, role }) => {
      await api.patch(`/api/sessions/${sessionId}/personas/${personaId}/backstory`, { backstory, name, role });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-requirements', projectId] });
    },
  });
};

function mapConsolidatedPersona(p: any): ProjectPersona {
  return {
    id: p.id || p.name,
    sessionId: (p.sourceSessionIds || [])[0] || '',
    name: p.name,
    role: p.role,
    goals: p.goals || [],
    painPoints: p.painPoints || [],
    context: p.context || '',
    createdAt: p.createdAt,
  };
}

function mapConsolidatedUserStory(s: any): ProjectUserStory {
  return {
    storyId: s.storyId || s.story,
    sessionId: (s.sourceSessionIds || [])[0],
    level: 'project' as const,
    persona: s.persona,
    story: s.story,
    acceptanceCriteria: s.acceptanceCriteria || [],
    priority: s.priority || 'medium',
    status: s.status || 'draft',
    generatedAt: s.generatedAt || s.createdAt || '',
    notes: s.notes,
    source: s.source,
    milestoneId: s.milestoneId,
    sourceSessionId: (s.sourceSessionIds || [])[0],
  };
}

function buildConsolidatedData(data: any): ProjectRequirementsData {
  return {
    personas: (data.personas || []).map(mapConsolidatedPersona),
    userStories: (data.userStories || []).map(mapConsolidatedUserStory),
    technicalSpecs: data.technicalSpecs || {
      coreFunctionality: [], integrationPoints: [], dataRequirements: [],
      securityConsiderations: [], performanceTargets: []
    },
    assumptions: data.assumptions || [],
    sessionCount: data.sessionCount || 0,
  };
}

export const useSyncRequirements = (projectId: string) => {
  const queryClient = useQueryClient();

  return useMutation<{ personaCount: number; storyCount: number }, Error>({
    mutationFn: async () => {
      const res = await api.post(`/api/projects/${projectId}/consolidate-requirements`);
      const data = res.data;

      // Inject the consolidated data directly into the query cache
      if (data.personas || data.userStories) {
        const consolidated: ProjectRequirementsData = buildConsolidatedData(data);
        queryClient.setQueryData(['project-requirements', projectId], consolidated);
      }

      return {
        personaCount: data.personaCount || 0,
        storyCount: data.storyCount || 0,
      };
    },
  });
};
